import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, ChevronDown, PawPrint } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { cartCount, openCart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowCategories(false);
    setShowUserMenu(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/shop' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Top Announcement Bar */}
      <div className="bg-brand-brown text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-end gap-6">
          <p className="hidden sm:block tracking-wide">
            <span className="font-semibold uppercase tracking-wider">Call Us:</span>{' '}
            <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="hover:text-brand-orange font-medium">{CONTACT_INFO.phone}</a>
          </p>
          <span className="hidden sm:block text-white/30">|</span>
          <p className="tracking-wide">
            <span className="font-semibold uppercase tracking-wider">Email:</span>{' '}
            <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-brand-orange font-medium break-all">{CONTACT_INFO.email}</a>
          </p>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-brand-orange flex items-center justify-center shadow-md ring-4 ring-white">
                <PawPrint className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-xl sm:text-2xl font-bold text-brand-brown tracking-tight font-display">
                  Paw Supplies Group
                </span>
                <span className="hidden sm:block text-[10px] tracking-[0.3em] uppercase text-brand-orange font-semibold">
                  Premium Pet Accessories
                </span>
              </div>
            </Link>

            {/* Desktop Nav - Pill Container */}
            <div className="hidden lg:flex items-center gap-2 bg-white rounded-full px-3 py-2 shadow-sm">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-5 py-2 rounded-full text-[13px] font-semibold uppercase tracking-wider transition-colors font-body ${
                      isActive
                        ? 'text-brand-orange'
                        : 'text-brand-brown hover:text-brand-orange'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}

              {/* Categories Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowCategories(true)}
                onMouseLeave={() => setShowCategories(false)}
              >
                <button className="px-5 py-2 rounded-full text-[13px] font-semibold uppercase tracking-wider text-brand-brown hover:text-brand-orange flex items-center gap-1 transition-colors font-body">
                  Categories
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {showCategories && (
                  <div className="absolute right-0 top-full pt-2 w-60 z-50">
                    <div className="bg-white rounded-2xl shadow-xl border border-brand-divider overflow-hidden py-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/shop?category=${cat.slug}`}
                          className="block px-5 py-2.5 text-sm font-semibold text-brand-brown hover:bg-brand-cream hover:text-brand-orange transition-colors font-body uppercase tracking-wider"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Cart Round Icon */}
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-full bg-brand-orange flex items-center justify-center text-white shadow-md hover:bg-brand-orange-dark transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-brand-brown text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-brand-cream">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Account */}
              <div
                className="relative hidden sm:block"
                onMouseEnter={() => setShowUserMenu(true)}
                onMouseLeave={() => setShowUserMenu(false)}
              >
                <button
                  onClick={() => isAuthenticated ? null : navigate('/login')}
                  className="w-11 h-11 rounded-full bg-white flex items-center justify-center text-brand-brown hover:text-brand-orange transition-colors shadow-sm"
                  aria-label="Account"
                >
                  <User className="w-5 h-5" />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full pt-2 w-56 z-50">
                    <div className="bg-white border border-brand-divider rounded-2xl shadow-xl overflow-hidden py-2">
                      {isAuthenticated ? (
                        <>
                          <div className="px-4 py-2 border-b border-brand-divider">
                            <p className="text-xs text-brand-brown-light">Signed in as</p>
                            <p className="text-sm font-semibold text-brand-brown truncate">{user?.email}</p>
                          </div>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-brand-brown hover:bg-brand-cream"
                          >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                          </button>
                        </>
                      ) : (
                        <>
                          <Link to="/login" className="block px-4 py-2.5 text-sm text-brand-brown hover:bg-brand-cream hover:text-brand-orange">Sign In</Link>
                          <Link to="/register" className="block px-4 py-2.5 text-sm text-brand-brown hover:bg-brand-cream hover:text-brand-orange">Create Account</Link>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Shop Now Pill Button */}
              <Link to="/shop" className="hidden md:inline-flex items-center gap-2 ml-2 px-7 h-11 rounded-full bg-brand-orange text-white text-sm font-semibold uppercase tracking-wider hover:bg-brand-orange-dark transition shadow-md font-display">
                Shop Now
              </Link>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-full bg-white flex items-center justify-center text-brand-brown"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-brand-divider">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-4 py-3 text-base font-semibold uppercase tracking-wider text-brand-brown hover:bg-brand-cream hover:text-brand-orange rounded-xl font-body"
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-3 border-t border-brand-divider mt-3">
                <p className="px-4 text-xs uppercase tracking-wider text-brand-orange font-bold mb-2">Categories</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.slug}`}
                    className="block px-4 py-2.5 text-sm text-brand-brown hover:bg-brand-cream hover:text-brand-orange rounded-xl font-medium uppercase tracking-wider"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <div className="pt-3 border-t border-brand-divider mt-3">
                {isAuthenticated ? (
                  <button onClick={handleLogout} className="block w-full text-left px-4 py-3 text-sm font-semibold text-brand-brown hover:bg-brand-cream rounded-xl">
                    Sign Out
                  </button>
                ) : (
                  <>
                    <Link to="/login" className="block px-4 py-3 text-sm font-semibold text-brand-brown hover:bg-brand-cream hover:text-brand-orange rounded-xl">Sign In</Link>
                    <Link to="/register" className="block px-4 py-3 text-sm font-semibold text-brand-brown hover:bg-brand-cream hover:text-brand-orange rounded-xl">Create Account</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
